import { createContext, useContext, useEffect, useState } from "react";

/**
 * Light-only theme system. The user asked for "no dark colours ever"
 * so `dark` and `ocean` (both dark palettes) were removed. What's left:
 *   • light   → default off-white
 *   • warm    → beige / cream
 *   • custom  → any hex the user picks, automatically pastel-ised so a
 *               bright red becomes soft pink, a saturated green becomes
 *               mint, etc. Guarantees the theme never turns oppressive
 *               even when the user picks a saturated colour by accident.
 *
 * `data-theme="custom"` on <html> activates the CSS variable path in
 * index.css. The picked hex is written as --custom-bg (body surface)
 * and --custom-surface (cards / inputs) so the whole app stays tinted
 * the same shade without per-component edits.
 */
const ALL_THEMES = [
  { id: "light",  label: "Light",  swatch: "#ffffff", flag: "theme_light" },
  { id: "warm",   label: "Warm",   swatch: "#f5e6ce", flag: "theme_warm"  },
];

const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
  themes: ALL_THEMES,
  customColor: "#e0f2fe",
  setCustomColor: () => {},
});

// Convert a hex like "#3366ff" to a soft pastel of the same hue.
// Uses HSL round-trip: keep hue, cap saturation to 45%, force lightness
// to 92%. The result is always a light tint suitable for a background.
function toPastel(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#eef2ff"; // safe fallback pastel indigo
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      default: h = ((r - g) / d + 4) * 60;
    }
  }
  // Force soft pastel: cap sat, force very high lightness.
  // Tuned 2026-07-29: previous defaults (L=0.92, sat=0.45) made every
  // picked colour look almost identical to plain white. Users couldn't
  // tell blue from pink at a glance. Bumped saturation up and lightness
  // slightly down so the tint is unmistakable while still light enough
  // to keep dark body text perfectly legible.
  const sPastel = Math.min(s, 0.65);
  const lPastel = 0.85;
  // Back to RGB
  const c = (1 - Math.abs(2 * lPastel - 1)) * sPastel;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const mLight = lPastel - c / 2;
  let [rr, gg, bb] = [0, 0, 0];
  if (h < 60)      [rr, gg, bb] = [c, x, 0];
  else if (h < 120) [rr, gg, bb] = [x, c, 0];
  else if (h < 180) [rr, gg, bb] = [0, c, x];
  else if (h < 240) [rr, gg, bb] = [0, x, c];
  else if (h < 300) [rr, gg, bb] = [x, 0, c];
  else               [rr, gg, bb] = [c, 0, x];
  const toHex = (v) => Math.round((v + mLight) * 255).toString(16).padStart(2, "0");
  return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
}

// Compute a slightly whiter surface tint on top of the base pastel so
// cards visually lift off the body.
function surfaceTint(pastelHex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(pastelHex || "");
  if (!m) return "#ffffff";
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  // Mix 25% white on top of the body pastel so cards land a clear step
  // brighter than the surrounding surface. Previously we mixed 60%
  // white, which made surfaces look almost the same as pure white —
  // users lost the tint on the parts they actually read (chat cards,
  // sidebars). 25% keeps surfaces visibly tinted and still legible.
  const mix = (v) => Math.round(v * 0.75 + 255 * 0.25);
  return `#${mix(r).toString(16).padStart(2, "0")}${mix(g).toString(16).padStart(2, "0")}${mix(b).toString(16).padStart(2, "0")}`;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const t = localStorage.getItem("lumioos_theme") || "light";
    // Migrate legacy dark/ocean users to light so we don't strand them.
    return t === "dark" || t === "ocean" ? "light" : t;
  });
  const [customColor, setCustomColorState] = useState(
    () => localStorage.getItem("lumioos_theme_custom") || "#e0f2fe"
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("lumioos_theme", theme);
    if (theme === "custom") {
      const pastel = toPastel(customColor);
      document.documentElement.style.setProperty("--custom-bg", pastel);
      document.documentElement.style.setProperty("--custom-surface", surfaceTint(pastel));
    } else {
      document.documentElement.style.removeProperty("--custom-bg");
      document.documentElement.style.removeProperty("--custom-surface");
    }
  }, [theme, customColor]);

  const setTheme = (t) => setThemeState(t);
  const setCustomColor = (c) => {
    setCustomColorState(c);
    localStorage.setItem("lumioos_theme_custom", c);
    // Switch into custom mode as soon as the user picks a colour.
    setThemeState("custom");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: ALL_THEMES, customColor, setCustomColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
export { ALL_THEMES as THEMES };
